<template>
  <nav class="navbar" :class="{ 'is-compact': compact, 'is-player': player }">
    <div class="navbar-container">
      <div class="navbar-left">
        <button
          v-if="showMobileMenuButton"
          @click="toggleMobileMenu"
          class="mobile-menu-button"
        >
          <span class="material-icons">menu</span>
        </button>
        <button type="button" class="navbar-logo" @click="goToHome">
          <img src="@/assets/logo.png" alt="Logo" class="logo">
          <span class="brand-name">GoodScriber</span>
        </button>
        <div v-if="showHomeNav" class="navbar-links" aria-label="Homepage navigation">
          <button
            v-for="item in navItems"
            :key="item.anchor || item.route"
            type="button"
            @click="goToNavItem(item)"
          >
            {{ item.label }}
          </button>
        </div>
      </div>
      <div class="navbar-right">
        <LanguageSelector />
        <UserMenu
          v-if="user"
          :user="user"
          :user-data-ready="userDataReady"
          :is-premium="userDetails?.isPremium"
          :is-pro="userDetails?.isPro"
          :remaining-usage="userDetails?.remainingUsage"
          :monthly-remaining-usage="userDetails?.monthlyRemainingUsage"
          :max-monthly-usage="userDetails?.maxMonthlyUsage"
          :purchased-credits="userDetails?.purchasedCredits"
          :purchased-credits-total="userDetails?.purchasedCreditsTotal"
          :can-manage-subscription="userDetails?.canManageSubscription"
          :premium-type="userDetails?.premiumType"
          :premium-expiry="userDetails?.premiumExpiry"
          @login="showLoginModal"
          @signup="showSignupModal"
          @logout="handleLogout"
          @upgrade="handleUpgrade"
        />
        <div v-else class="auth-buttons">
          <button @click="showLoginModal" class="btn btn-login">
            <span class="material-icons">login</span>
            <span class="auth-label">{{ $t('navbar.login') }}</span>
          </button>
          <button @click="showSignupModal" class="btn btn-signup">
            <span class="material-icons">person_add</span>
            <span class="auth-label">{{ $t('navbar.signup') }}</span>
          </button>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { isHomePath } from '@/router'
import { useI18n } from 'vue-i18n'
import LanguageSelector from '../common/LanguageSelector.vue'
import UserMenu from '../common/UserMenu.vue'
import { useAuth } from '@/composables/auth'
import { useModals } from '@/composables/modals'
import { useViews } from '@/composables/views'
import { useMobileMenu } from '@/composables/useMobileMenu'

export default {
  name: 'NavBar',
  components: {
    LanguageSelector,
    UserMenu
  },
  props: {
    compact: { type: Boolean, default: false },
    player: { type: Boolean, default: false }
  },
  setup() {
    const router = useRouter()
    const { t, locale } = useI18n()
    const { user, userDetails, userDataReady, logout } = useAuth()
    const { showLoginModal, showSignupModal, handleUpgrade } = useModals()
    const { currentView, handleViewChange } = useViews()
    const { showMobileMenuButton, toggleMobileMenu, closeMobileMenu } = useMobileMenu()
    const showHomeNav = computed(() => isHomePath(router.currentRoute.value.path) && currentView.value === 'HomePage')

    const scrollToHomeTop = () => {
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const scrollingElement = document.scrollingElement || document.documentElement
          scrollingElement.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        })
      })
    }

    const goToHome = async () => {
      closeMobileMenu()
      if (!isHomePath(router.currentRoute.value.path)) {
        await router.push('/')
      }
      handleViewChange('HomePage')
      scrollToHomeTop()
    }

    const navItems = computed(() => [
      { label: t('navbar.library'), anchor: 'library' },
      { label: t('navbar.workflow'), anchor: 'workflow' },
      { label: t('navbar.product'), anchor: 'features' },
      { label: t('navbar.pricing'), anchor: 'pricing' },
      { label: t('navbar.resources'), anchor: 'faq' },
      {
        label: String(locale.value || '').toLowerCase().startsWith('zh') ? '学习指南' : 'Guides',
        route: String(locale.value || '').toLowerCase().startsWith('zh') ? '/zh/learn' : '/learn'
      }
    ])

    const goToNavItem = async (item) => {
      if (item.route) {
        closeMobileMenu()
        await router.push(item.route)
        return
      }
      await goToAnchor(item.anchor)
    }

    const goToAnchor = async (anchor) => {
      closeMobileMenu()
      if (!isHomePath(router.currentRoute.value.path)) {
        await router.push('/')
        handleViewChange('HomePage')
      }
      window.requestAnimationFrame(() => {
        document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      })
    }

    const handleLogout = async () => {
      try {
        await logout()
        goToHome()
      } catch {}
    }

    return {
      user,
      userDetails,
      userDataReady,
      showMobileMenuButton,
      toggleMobileMenu,
      goToHome,
      handleLogout,
      navItems,
      goToNavItem,
      showHomeNav,
      showLoginModal,
      showSignupModal,
      handleUpgrade
    }
  }
}
</script>

<style scoped>
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: var(--navbar-height);
  background:
    linear-gradient(180deg, rgba(247, 249, 241, 0.88), rgba(251, 252, 247, 0.76)),
    linear-gradient(90deg, rgba(23, 107, 77, 0.08), rgba(53, 199, 201, 0.06), rgba(255, 209, 102, 0.05));
  color: var(--color-text);
  box-shadow: 0 12px 30px rgba(17, 24, 39, 0.06);
  backdrop-filter: blur(18px) saturate(150%);
  -webkit-backdrop-filter: blur(18px) saturate(150%);
  border-bottom: 1px solid rgba(23, 107, 77, 0.12);
  transition: all 0.3s ease;
}

.navbar-container {
  max-width: 1240px;
  margin: 0 auto;
  padding: 0 2rem;
  height: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  min-width: 0;
}

.navbar-left {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1rem;
  min-width: 0;
}

.mobile-menu-button {
  display: none;
  background: none;
  border: none;
  color: var(--color-brand-500);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--radius-sm);
  transition: background-color 0.2s ease;
}

.mobile-menu-button:hover {
  background-color: var(--color-brand-50);
}

.mobile-menu-button .material-icons {
  font-size: 1.5rem;
}

.navbar-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 1rem;
  min-width: 0;
}

.navbar-logo {
  display: flex;
  align-items: center;
  gap: 0.62rem;
  padding: 0;
  border: 0;
  background: transparent;
  font: inherit;
  cursor: pointer;
  opacity: 1;
  transition: transform 0.2s ease;
}

.navbar-logo:hover {
  transform: translateY(-1px);
}

.logo {
  height: 2rem;
  width: 2rem;
  object-fit: cover;
  clip-path: circle(50% at center);
}

.brand-name {
  color: var(--color-text);
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.navbar-links {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 1.2rem;
  margin-left: 1.1rem;
}

.navbar-links button {
  min-height: 32px;
  padding: 0;
  border: 0;
  color: #475569;
  background: transparent;
  font: inherit;
  font-size: 0.86rem;
  font-weight: 720;
  cursor: pointer;
  transition: color 0.18s ease, background 0.18s ease, transform 0.18s ease;
}

.navbar-links button:hover {
  color: #0f172a;
  background: transparent;
  transform: translateY(-1px);
}

.auth-buttons {
  display: flex;
  gap: 0.55rem;
  align-items: center;
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: auto;
  height: 38px;
  padding: 0 0.95rem;
  border-radius: var(--radius-pill);
  border: none;
  cursor: pointer;
  transition: background 0.18s ease, border-color 0.18s ease, color 0.18s ease, transform 0.18s ease;
}

.btn .material-icons {
  font-size: 18px;
}

.auth-label {
  font-size: 0.85rem;
  font-weight: 750;
}

.btn-login {
  background-color: rgba(255, 255, 255, 0.36);
  color: var(--color-text);
  border: 1px solid rgba(23, 107, 77, 0.18);
}

.btn-login:hover {
  color: #0f172a;
  background-color: rgba(255, 255, 255, 0.72);
  border-color: rgba(23, 107, 77, 0.32);
}

.btn-signup {
  background-color: #111827;
  color: #ffffff;
  box-shadow: 0 14px 28px rgba(15, 23, 42, 0.14);
}

.btn-signup:hover {
  background-color: #0f172a;
  transform: translateY(-1px);
}

:deep(.language-toggle) {
  min-height: 34px;
  padding: 0 0.78rem;
  border-color: rgba(23, 107, 77, 0.16);
  border-radius: 999px;
  color: #334155;
  background: rgba(255, 255, 255, 0.52);
  font-weight: 760;
  margin-right: 0;
}

/* 工作台：矮顶栏，logo 与侧边栏菜单左对齐 */
.navbar.is-compact {
  height: var(--app-navbar-height);
  background: var(--color-page);
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  border-bottom-color: var(--border-subtle);
}

.navbar.is-compact .navbar-container {
  max-width: none;
  padding: 0 var(--space-5) 0 var(--space-6);
}

.navbar.is-compact .logo {
  height: 1.75rem;
  width: 1.75rem;
}

@media (max-width: 1024px) {
  .navbar-container {
    padding: 0 1rem;
  }
  
  .navbar-links {
    gap: 0.8rem;
    margin-left: 0.7rem;
  }

  .navbar-links button {
    font-size: 0.8rem;
  }
  
  .btn {
    height: 34px;
    padding: 0 0.72rem;
  }
  
  .btn .material-icons {
    font-size: 16px;
    width: 16px;
    height: 16px;
  }
}

@media (max-width: 768px) {
  /* 手机端播放页：播放器自带返回栏，网站顶栏让出屏幕空间 */
  .navbar.is-player {
    display: none;
  }

  .navbar {
    height: 3.5rem;
    background:
      linear-gradient(180deg, rgba(247, 249, 241, 0.92), rgba(251, 252, 247, 0.82)),
      linear-gradient(90deg, rgba(23, 107, 77, 0.08), rgba(53, 199, 201, 0.06));
  }

  .navbar-container,
  .navbar.is-compact .navbar-container {
    display: flex;
    justify-content: space-between;
    padding: 0 0.875rem;
    gap: 0.75rem;
  }

  .navbar-links {
    display: none;
  }

  .navbar-left {
    flex: 0 0 auto;
  }

  .mobile-menu-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    padding: 0;
    flex: 0 0 34px;
  }

  .logo {
    height: 2.1rem;
    width: 2.1rem;
  }

  .brand-name,
  .auth-label {
    display: none;
  }

  .auth-buttons {
    gap: 0.5rem;
  }

  .btn {
    width: 34px;
    height: 34px;
    padding: 0;
    min-width: unset;
    min-height: unset;
  }

  .btn .material-icons {
    font-size: 18px;
  }

  .navbar-right {
    gap: 0.6rem;
    margin-left: auto;
  }
}
</style>
